import {
  useQueries,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'
import api from '@/services/api'
import type {
  Forklift,
  ForkliftListResponse,
  ForkliftStats,
  ScenarioRunsResponse,
  ErrorEventsResponse,
  ScenarioStats,
} from '@/types/forklift'
import { mergeForkliftStats, mergeScenarioStats } from '@/common/mergeStats'

interface ForkliftListParams {
  site_id?: string
  status?: string
  page?: number
  limit?: number
  period?: string // '7d', '30d', '90d', '365d', 'all'
  show_sim?: string // 'true' | 'false'
}

interface ForkliftStatsParams {
  forkId: string
  start_date: string
  end_date: string
  granularity?: 'hour' | 'day' | 'week'
}

// フォークリフト一覧を取得
export function useForklifts(params: ForkliftListParams = {}) {
  return useQuery({
    queryKey: ['forklifts', params],
    queryFn: async () => {
      const { data } = await api.get<ForkliftListResponse>('/forklifts', {
        params,
      })
      return data
    },
    staleTime: 1000 * 30, // 30秒
  })
}

// 個別フォークリフト情報を取得
export function useForklift(forkId: string | undefined) {
  return useQuery({
    queryKey: ['forklift', forkId],
    queryFn: async () => {
      const { data } = await api.get<Forklift>(`/forklifts/${forkId}`)
      return data
    },
    enabled: !!forkId,
    staleTime: 1000 * 60 * 5, // 5分
  })
}

// フォークリフトの統計を取得
export function useForkliftStats({
  forkId,
  start_date,
  end_date,
  granularity = 'day',
}: ForkliftStatsParams) {
  return useQuery({
    queryKey: ['forklift-stats', forkId, start_date, end_date, granularity],
    queryFn: async () => {
      const { data } = await api.get<ForkliftStats>(
        `/forklifts/${forkId}/stats`,
        {
          params: { start_date, end_date, granularity },
        }
      )
      return data
    },
    enabled: !!forkId && !!start_date && !!end_date,
    staleTime: 1000 * 60 * 5, // 5分
  })
}

interface ScenarioStatsParams {
  forkId: string
  start_date: string
  end_date: string
}

// フォークリフトのシナリオ統計を取得
export function useScenarioStats({
  forkId,
  start_date,
  end_date,
}: ScenarioStatsParams) {
  return useQuery({
    queryKey: ['scenario-stats', forkId, start_date, end_date],
    queryFn: async () => {
      const { data } = await api.get<ScenarioStats>(
        `/forklifts/${forkId}/scenario-stats`,
        {
          params: { start_date, end_date },
        }
      )
      return data
    },
    enabled: !!forkId && !!start_date && !!end_date,
    staleTime: 1000 * 60 * 5, // 5分
  })
}

async function fetchForkliftStats(
  forkId: string,
  start_date: string,
  end_date: string,
  granularity: 'hour' | 'day' | 'week'
): Promise<ForkliftStats> {
  const { data } = await api.get<ForkliftStats>(`/forklifts/${forkId}/stats`, {
    params: { start_date, end_date, granularity },
  })
  return data
}

async function fetchScenarioStats(
  forkId: string,
  start_date: string,
  end_date: string
): Promise<ScenarioStats> {
  const { data } = await api.get<ScenarioStats>(
    `/forklifts/${forkId}/scenario-stats`,
    {
      params: { start_date, end_date },
    }
  )
  return data
}

interface MultiForkStatsParams {
  forkIds: string[]
  start_date: string
  end_date: string
}

interface MultiForkStatsResult<T> {
  // 全機体分が揃ったときだけ合算結果を返す (1 機体でも未取得なら undefined)
  data: T | undefined
  isLoading: boolean
  isFetching: boolean
  isError: boolean
}

// useQueries の combine はモジュールスコープの関数を渡すと結果が変わったときだけ再計算される。
// 合算 (merge) をここで行い、再レンダーのたびに合算し直さないようにする
function combineMultiResults<T>(
  merge: (list: T[]) => T
): (results: UseQueryResult<T>[]) => MultiForkStatsResult<T> {
  return (results) => {
    const loaded = results.map((r) => r.data)
    const complete =
      results.length > 0 && loaded.every((d): d is T => d !== undefined)
    return {
      data: complete ? merge(loaded) : undefined,
      isLoading: results.some((r) => r.isLoading),
      isFetching: results.some((r) => r.isFetching),
      isError: results.some((r) => r.isError),
    }
  }
}

const combineForkliftStats = combineMultiResults(mergeForkliftStats)
const combineScenarioStats = combineMultiResults(mergeScenarioStats)

// 複数機体の統計を機体ごとに並列取得して合算する (メトリクス画面の「すべて」/ 複数選択)。
// queryKey は単体用フックと同じなので、単体で開いた結果のキャッシュをそのまま使える
export function useMultiForkliftStats({
  forkIds,
  start_date,
  end_date,
}: MultiForkStatsParams): MultiForkStatsResult<ForkliftStats> {
  const granularity = 'day'
  return useQueries({
    queries: forkIds.map((forkId) => ({
      queryKey: ['forklift-stats', forkId, start_date, end_date, granularity],
      queryFn: () =>
        fetchForkliftStats(forkId, start_date, end_date, granularity),
      enabled: !!forkId && !!start_date && !!end_date,
      staleTime: 1000 * 60 * 5, // 5分
    })),
    combine: combineForkliftStats,
  })
}

export function useMultiScenarioStats({
  forkIds,
  start_date,
  end_date,
}: MultiForkStatsParams): MultiForkStatsResult<ScenarioStats> {
  return useQueries({
    queries: forkIds.map((forkId) => ({
      queryKey: ['scenario-stats', forkId, start_date, end_date],
      queryFn: () => fetchScenarioStats(forkId, start_date, end_date),
      enabled: !!forkId && !!start_date && !!end_date,
      staleTime: 1000 * 60 * 5, // 5分
    })),
    combine: combineScenarioStats,
  })
}

// シナリオ実行単位の行を取得 (メトリクス画面の CSV ダウンロード用)
// 旧ブリッジ機体は webapp ログから合成するため期間が長いと数十秒かかる。
// メトリクス画面は CSV ダウンロード時にだけ呼ぶ
// scenario-runs は 1 回の API 応答に最大このくらいの日数を含める。
// 1 年分を 1 回で返すと busy な機体 (1 日 60 実行超) では Lambda の応答上限 6 MB を
// 超えるため、期間を分割して並列取得し、結果を連結する。run_no と累積は各チャンクが
// 期間開始日より前の daily-stats をベースラインにして振るので、連結しても連続する
const SCENARIO_RUNS_CHUNK_DAYS = 31
const SCENARIO_RUNS_CONCURRENCY = 4

function splitDateRange(start: string, end: string, days: number) {
  const DAY_MS = 24 * 60 * 60 * 1000
  const ranges: { start: string; end: string }[] = []
  const endMs = Date.parse(`${end}T00:00:00Z`)
  for (
    let t = Date.parse(`${start}T00:00:00Z`);
    t <= endMs;
    t += days * DAY_MS
  ) {
    const chunkEnd = Math.min(endMs, t + (days - 1) * DAY_MS)
    ranges.push({
      start: new Date(t).toISOString().slice(0, 10),
      end: new Date(chunkEnd).toISOString().slice(0, 10),
    })
  }
  return ranges
}

async function fetchScenarioRunsRange(
  forkId: string,
  start_date: string,
  end_date: string
): Promise<ScenarioRunsResponse> {
  const { data } = await api.get<ScenarioRunsResponse>(
    `/forklifts/${forkId}/scenario-runs`,
    {
      params: { start_date, end_date },
    }
  )
  return data
}

export async function fetchScenarioRuns({
  forkId,
  start_date,
  end_date,
}: ScenarioStatsParams): Promise<ScenarioRunsResponse> {
  const ranges = splitDateRange(start_date, end_date, SCENARIO_RUNS_CHUNK_DAYS)
  if (ranges.length <= 1) {
    return fetchScenarioRunsRange(forkId, start_date, end_date)
  }
  const results: ScenarioRunsResponse[] = new Array(ranges.length)
  for (let i = 0; i < ranges.length; i += SCENARIO_RUNS_CONCURRENCY) {
    const batch = ranges.slice(i, i + SCENARIO_RUNS_CONCURRENCY)
    const settled = await Promise.all(
      batch.map((r) => fetchScenarioRunsRange(forkId, r.start, r.end))
    )
    settled.forEach((res, j) => {
      results[i + j] = res
    })
  }
  const runs = results.flatMap((r) => r.runs)
  const kinds = new Set(runs.map((r) => r.source))
  const source: ScenarioRunsResponse['source'] =
    kinds.size === 0 ? 'none' : kinds.size === 1 ? [...kinds][0] : 'mixed'
  return {
    fork_id: forkId,
    period: { start: start_date, end: end_date },
    source,
    // 累積のベースラインは先頭チャンクのもの (期間開始日より前の合計)
    baseline: results[0].baseline,
    runs,
  }
}

// エラー発生 1 件ごとの行 (メトリクス画面「エラー別集計」の CSV 用)。
// 件数は多くても数千なので期間分割はしない
export async function fetchErrorEvents({
  forkId,
  start_date,
  end_date,
}: ScenarioStatsParams): Promise<ErrorEventsResponse> {
  const { data } = await api.get<ErrorEventsResponse>(
    `/forklifts/${forkId}/error-events`,
    {
      params: { start_date, end_date },
    }
  )
  return data
}

export function useScenarioRuns(params: ScenarioStatsParams) {
  const { forkId, start_date, end_date } = params
  return useQuery({
    queryKey: ['scenario-runs', forkId, start_date, end_date],
    queryFn: () => fetchScenarioRuns(params),
    enabled: !!forkId && !!start_date && !!end_date,
    staleTime: 1000 * 60 * 5, // 5分
  })
}
